import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./ProjectDetail.css";
import { getProjectById, PROJECTS } from "./projectsData";
import PasswordGate, { isProjectUnlocked } from "./PasswordGate";

function getSectionStatusLabel(status, fallback) {
  if (status === "future") return "About to be built";
  if (status === "in-progress") return "In progress";
  if (status === "done") return "Completed";
  return fallback || "Completed";
}

function ProjectStepNav({ sections, activeIndex, onJump }) {
  return (
    <nav className="project-step-nav" aria-label="Project sections">
      {sections.map((section, index) => {
        const status = section.status || "done";
        const isRead = status === "done" && index < activeIndex;
        const isActive = index === activeIndex;
        const isFuture = status === "future";
        const isInProgress = status === "in-progress";
        const isUnavailable = isFuture || isInProgress;

        return (
          <button
            key={section.id}
            type="button"
            className={[
              "project-step",
              isRead ? "is-read" : "",
              isActive ? "is-active" : "",
              isUnavailable ? "is-unavailable" : "",
              isFuture ? "is-future" : "",
              isInProgress ? "is-in-progress" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => onJump(section.id)}
          >
            <span className="project-step__marker" aria-hidden="true">
              {isRead ? "✓" : isFuture ? "!" : ""}
            </span>

            <span className="project-step__copy">
              <strong>{section.sidebarTitle || section.eyebrow}</strong>
              <small>
                {section.sidebarSubtitle ||
                  getSectionStatusLabel(status, section.subtitle)}
              </small>
            </span>
          </button>
        );
      })}
    </nav>
  );
}

function ProjectMobileProgress({ sections, activeIndex, onJump, onBack }) {
  const [open, setOpen] = useState(false);
  const activeSection = sections[activeIndex] || sections[0];

  return (
    <div className="project-mobile-progress">
      <button className="project-mobile-back" type="button" onClick={onBack}>
        ← Go Back
      </button>

      <section className={`project-mobile-nav ${open ? "is-open" : ""}`}>
        <button
          className="project-mobile-nav__toggle"
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-label={
            open ? "Minimize project navigation" : "Expand project navigation"
          }
        >
          {open ? "−" : "+"}
        </button>

        {!open ? (
          <div className="project-mobile-current">
            <span className="project-mobile-current__kicker">
              You’re Currently at
            </span>

            <span className="project-mobile-current__marker" aria-hidden="true" />

            <span className="project-mobile-current__copy">
              <strong>
                {activeSection?.sidebarTitle || activeSection?.eyebrow}
              </strong>
              <small>
                {activeSection?.sidebarSubtitle ||
                  getSectionStatusLabel(
                    activeSection?.status,
                    activeSection?.subtitle
                  )}
              </small>
            </span>
          </div>
        ) : (
          <ProjectStepNav
            sections={sections}
            activeIndex={activeIndex}
            onJump={(id) => {
              onJump(id);
              setOpen(false);
            }}
          />
        )}
      </section>
    </div>
  );
}

function Slideshow({ images = [] }) {
  const [index, setIndex] = useState(0);
  const current = images[index] || images[0];

  if (!images.length) return null;

  return (
    <div className="project-slideshow">
      <button
        type="button"
        onClick={() => setIndex((value) => Math.max(value - 1, 0))}
        disabled={index === 0}
        aria-label="Previous image"
      >
        ←
      </button>

      <img src={current} alt="" />

      <button
        type="button"
        onClick={() =>
          setIndex((value) => Math.min(value + 1, images.length - 1))
        }
        disabled={index === images.length - 1}
        aria-label="Next image"
      >
        →
      </button>

      <span>
        {index + 1} / {images.length}
      </span>
    </div>
  );
}

function OptionsBlock({ options = [] }) {
  const [selected, setSelected] = useState(0);
  const active = options[selected] || options[0];

  if (!options.length) return null;

  return (
    <div className="project-options-layout">
      <div className="project-options-list">
        {options.map((option, index) => (
          <button
            key={option.title}
            type="button"
            className={selected === index ? "is-active" : ""}
            onClick={() => setSelected(index)}
          >
            <strong>{option.title}</strong>
            <span>{option.body}</span>
          </button>
        ))}
      </div>

      <div className="project-options-preview">
        {active?.image && <img src={active.image} alt="" />}
      </div>
    </div>
  );
}

function MediaOptionsBlock({ options = [] }) {
  const [selected, setSelected] = useState(0);
  const active = options[selected] || options[0];

  if (!options.length) return null;

  const media = active?.media || active?.image || active?.video;
  const isVideo =
    typeof media === "string" &&
    (media.endsWith(".mp4") ||
      media.endsWith(".webm") ||
      media.endsWith(".ogg"));

  return (
    <div className="project-options-layout">
      <div className="project-options-list">
        {options.map((option, index) => (
          <button
            key={option.title}
            type="button"
            className={selected === index ? "is-active" : ""}
            onClick={() => setSelected(index)}
          >
            <strong>{option.title}</strong>
            <span>{option.body}</span>
          </button>
        ))}
      </div>

      <div className="project-options-preview">
        {media &&
          (isVideo ? (
            <video src={media} autoPlay muted loop playsInline />
          ) : (
            <img src={media} alt="" />
          ))}
      </div>
    </div>
  );
}

function BlogBlock({ section }) {
  const paragraphs = (section.content || "")
    .trim()
    .split(/\n\s*\n/)
    .filter(Boolean);

  return (
    <div className="project-blog-layout">
      {section.introImage && (
        <div className="project-image-frame">
          <img src={section.introImage} alt="" />
        </div>
      )}

      {paragraphs.length > 0 && (
        <div className="project-blog-content">
          {paragraphs.map((paragraph, index) => (
            <p key={index} className="project-section-body">
              {paragraph}
            </p>
          ))}
        </div>
      )}

      {section.outroImage && (
        <div className="project-image-frame">
          <img src={section.outroImage} alt="" />
        </div>
      )}
    </div>
  );
}

function RationaleListBlock({ items = [] }) {
  if (!items.length) return null;

  return (
    <div className="project-rationale-list">
      {items.map((item) => (
        <article className="project-rationale-row" key={item.label}>
          <div className="project-rationale-label">{item.label}</div>
          <div className="project-rationale-copy">
            <h4>{item.title}</h4>
            <p>{item.body}</p>
          </div>
        </article>
      ))}
    </div>
  );
}

function SplitInsightsBlock({ section }) {
  return (
    <div className="project-split-insights">
      {section.columns?.map((column) => (
        <div className="project-split-insights__column" key={column.title}>
          <p className="project-split-insights__title">{column.title}</p>

          <div className="project-split-insights__list">
            {column.items?.map((item) => (
              <article
                className="project-split-insights__item"
                key={item.label || item.tag}
              >
                {item.label && (
                  <h4 className="project-split-insights__label">{item.label}</h4>
                )}

                {item.tag && (
                  <span className="project-split-insights__tag">{item.tag}</span>
                )}

                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function QuotePointsBlock({ section }) {
  return (
    <div className="project-quote-points">
      {section.quoteNote && (
        <div className="project-quote-points__note">{section.quoteNote}</div>
      )}

      <div className="project-quote-points__list">
        {section.items?.map((item) => (
          <article className="project-quote-points__item" key={item.quote}>
            <blockquote>{item.quote}</blockquote>
            <p>{item.body}</p>
          </article>
        ))}
      </div>
    </div>
  );
}

function ImagePointsBlock({ section }) {
  return (
    <div className="project-image-points">
      {section.image && (
        <div className="project-image-frame project-image-points__image">
          <img src={section.image} alt="" />
        </div>
      )}

      <div className="project-image-points__list">
        {section.points?.map((point, index) => (
          <div className="project-image-points__item" key={index}>
            <span className="project-image-points__bullet" />
            <p>{point}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function TextListBlock({ section }) {
  return (
    <div
      className={`project-text-list ${section.tone === "dark" ? "is-dark" : ""}`}
    >
      {section.items?.map((item) => (
        <article className="project-text-list__item" key={item.title}>
          <h4>{item.title}</h4>
          <p>{item.body}</p>
        </article>
      ))}
    </div>
  );
}

function SingleMediaBlock({ section }) {
  const mediaImage = section.image;
  const mediaVideo = section.video;
  const hasVideo = Boolean(mediaVideo);

  if (!mediaImage && !mediaVideo) return null;

  return (
    <div
      className="project-image-frame project-single-media-frame"
      style={
        mediaImage
          ? { backgroundImage: `url(${mediaImage})` }
          : undefined
      }
    >
      {mediaImage && (
        <img
          src={mediaImage}
          alt=""
          className={`project-single-media-fallback ${hasVideo ? "is-hidden" : ""}`}
        />
      )}

      {hasVideo && (
        <video
          className="project-single-media-video"
          src={mediaVideo}
          poster={mediaImage}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        />
      )}
    </div>
  );
}

function StackedMediaCardSlideshow({ images = [], title = "" }) {
  const [index, setIndex] = useState(0);
  const current = images[index] || images[0];

  useEffect(() => {
    if (images.length <= 1) return undefined;

    const timer = window.setInterval(() => {
      setIndex((value) => (value + 1) % images.length);
    }, 4000);

    return () => window.clearInterval(timer);
  }, [images]);

  if (!images.length) return null;

  return (
    <div className="project-stacked-media-slideshow">
      <button
        type="button"
        className="project-stacked-media-slideshow__nav is-left"
        onClick={() => setIndex((value) => Math.max(value - 1, 0))}
        disabled={index === 0}
        aria-label={`Previous ${title} image`}
      >
        ←
      </button>

      <img src={current} alt={title} />

      <button
        type="button"
        className="project-stacked-media-slideshow__nav is-right"
        onClick={() =>
          setIndex((value) => Math.min(value + 1, images.length - 1))
        }
        disabled={index === images.length - 1}
        aria-label={`Next ${title} image`}
      >
        →
      </button>

      {images.length > 1 && (
        <span className="project-stacked-media-slideshow__count">
          {index + 1} / {images.length}
        </span>
      )}
    </div>
  );
}

function StackedMediaListBlock({ section }) {
  if (!section.items?.length) return null;

  return (
    <div className="project-stacked-media-list">
      {section.items.map((item, index) => (
        <article
          className="project-stacked-media-list__item"
          key={item.title || index}
        >
          <div className="project-stacked-media-list__media">
            {item.images?.length ? (
              <StackedMediaCardSlideshow
                images={item.images}
                title={item.title || ""}
              />
            ) : item.image ? (
              <img src={item.image} alt={item.title || ""} />
            ) : null}
          </div>

          <div className="project-stacked-media-list__content">
            <div className="project-stacked-media-list__index">
              {String(index + 1).padStart(2, "0")}
            </div>

            <h4>{item.title}</h4>

            {item.subtitle && (
              <p className="project-stacked-media-list__subtitle">
                {item.subtitle}
              </p>
            )}

            {item.points?.length > 0 && (
              <ul className="project-stacked-media-list__points">
                {item.points.map((point, pointIndex) => (
                  <li
                    key={pointIndex}
                    className={
                      point.type === "positive"
                        ? "is-positive"
                        : point.type === "negative"
                        ? "is-negative"
                        : ""
                    }
                  >
                    {point.text}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </article>
      ))}
    </div>
  );
}

function ProjectSectionBlock({ section }) {
  return (
    <section className="project-story-section" id={section.id}>
      <div className="project-story-section__inner">
        <p className="project-section-eyebrow">{section.eyebrow}</p>
        <h2>{section.title}</h2>

        {section.subtitle && <h3>{section.subtitle}</h3>}

        {section.body &&
          section.layout !== "status" &&
          section.layout !== "blog" &&
          section.layout !== "rationale-list" &&
          section.layout !== "split-insights" &&
          section.layout !== "quote-points" &&
          section.layout !== "image-points" &&
          section.layout !== "text-list" &&
          section.layout !== "media-options" &&
          section.layout !== "stacked-media-list" && (
            <p className="project-section-body">{section.body}</p>
          )}

        {section.layout === "blog" && <BlogBlock section={section} />}

        {section.layout === "single-image" && <SingleMediaBlock section={section} />}

        {section.layout === "slideshow" && <Slideshow images={section.images} />}

        {section.layout === "options" && (
          <OptionsBlock options={section.options} />
        )}

        {section.layout === "media-options" && (
          <MediaOptionsBlock options={section.options} />
        )}

        {section.layout === "rationale-list" && (
          <RationaleListBlock items={section.items} />
        )}

        {section.layout === "split-insights" && (
          <SplitInsightsBlock section={section} />
        )}

        {section.layout === "quote-points" && (
          <QuotePointsBlock section={section} />
        )}

        {section.layout === "image-points" && (
          <ImagePointsBlock section={section} />
        )}

        {section.layout === "text-list" && <TextListBlock section={section} />}

        {section.layout === "stacked-media-list" && (
          <StackedMediaListBlock section={section} />
        )}

        {section.layout === "status" && (
          <div
            className={`project-status-card project-status-card--${
              section.state || section.status || "future"
            }`}
          >
            <span>
              {(section.state || section.status) === "in-progress"
                ? "In progress"
                : "Yet to be built"}
            </span>
            <p>{section.body}</p>
          </div>
        )}
      </div>
    </section>
  );
}

function RelatedProjectsSection({
  currentProjectId,
  currentProjectType,
  onOpenProject,
}) {
  const relatedProjects = useMemo(() => {
    const sameTypeProjects = PROJECTS.filter(
      (item) => item.id !== currentProjectId && item.type === currentProjectType
    );

    if (sameTypeProjects.length >= 3) {
      return sameTypeProjects;
    }

    const fallbackProjects = PROJECTS.filter(
      (item) => item.id !== currentProjectId && item.type !== currentProjectType
    );

    return [...sameTypeProjects, ...fallbackProjects];
  }, [currentProjectId, currentProjectType]);

  if (!relatedProjects.length) return null;

  return (
    <section className="project-related-section">
      <div className="project-related-head">
        <p className="project-section-eyebrow">More Work</p>
        <h2>Check These Too</h2>
        <p className="project-section-body">
          A few more things you might want to explore next.
        </p>
      </div>

      <div className="project-related-grid">
        {relatedProjects.map((card) => {
          const isLocked = card.locked && !isProjectUnlocked(card.id);

          return (
            <article
              key={card.id}
              className="project-related-card"
              data-platform
              onClick={() => onOpenProject(card)}
              style={{
                opacity: isLocked ? 0.72 : 1,
                cursor: card.link ? "pointer" : "default",
              }}
            >
              <div className="project-related-card__media">
                {isLocked ? (
                  <img
                    src={card.lockedPreview || card.image}
                    alt=""
                    style={{ filter: "blur(6px)" }}
                  />
                ) : card.screen?.endsWith(".mp4") ? (
                  <video src={card.screen} autoPlay muted loop playsInline />
                ) : (
                  <img src={card.screen || card.image} alt={card.title} />
                )}

                {isLocked && (
                  <div className="project-related-card__overlay">
                    <span className="project-related-card__lock">🔒</span>
                    <span className="project-related-card__overlayText">
                      Protected
                    </span>
                  </div>
                )}
              </div>

              {card.tags && (
                <div className="project-related-card__tags">
                  {card.tags.map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>
              )}

              <h3>{card.title}</h3>
              <p>{card.description}</p>

              {isLocked && (
                <small className="project-related-card__lockedText">
                  Password required
                </small>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const pageRef = useRef(null);

  const project = getProjectById(projectId);
  const sections = useMemo(() => {
    return (project?.sections || []).filter((section) => Boolean(section.id));
  }, [project]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [unlocked, setUnlocked] = useState(() =>
    project ? isProjectUnlocked(project.id) : false
  );
  const [gateProject, setGateProject] = useState(null);

  useEffect(() => {
    setActiveIndex(0);
    setUnlocked(project ? isProjectUnlocked(project.id) : false);
  }, [projectId, project]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, [projectId]);

  useEffect(() => {
    if (!sections.length || (project?.locked && !unlocked)) return undefined;

    const updateActiveSection = () => {
      const activationLine = window.innerHeight * 0.48;
      let nextIndex = 0;

      sections.forEach((section, index) => {
        const node = document.getElementById(section.id);
        if (!node) return;

        const rect = node.getBoundingClientRect();

        if (rect.top <= activationLine) {
          nextIndex = index;
        }
      });

      setActiveIndex(nextIndex);
    };

    updateActiveSection();

    window.addEventListener("scroll", updateActiveSection, { passive: true });
    window.addEventListener("resize", updateActiveSection);

    return () => {
      window.removeEventListener("scroll", updateActiveSection);
      window.removeEventListener("resize", updateActiveSection);
    };
  }, [sections, project, unlocked]);

  const jumpToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const openProjectCard = (card) => {
    if (!card.link) return;

    if (card.locked && !isProjectUnlocked(card.id)) {
      setGateProject(card);
      return;
    }

    navigate(card.link);
  };

  if (!project) {
    return (
      <main className="project-detail project-detail--missing">
        <button onClick={() => navigate("/featured-projects")}>← Back</button>
        <h1>Project not found</h1>
      </main>
    );
  }

  if (project.locked && !unlocked) {
    return (
      <main className="project-detail project-detail--missing">
        <PasswordGate
          projectId={project.id}
          projectTitle={project.title}
          onClose={() => navigate(-1)}
          onUnlock={() => setUnlocked(true)}
        />
      </main>
    );
  }

  if (!sections.length) {
    return (
      <main className="project-detail project-detail--missing">
        <button onClick={() => navigate("/featured-projects")}>← Back</button>
        <h1>{project.title}</h1>
        <p>This project page is yet to be built.</p>
      </main>
    );
  }

  return (
    <>
      <main className="project-detail" ref={pageRef}>
        <aside className="project-progress-rail">
          <div className="project-progress-panel">
            <button className="project-back" onClick={() => navigate(-1)}>
              ← Go Back
            </button>

            <ProjectStepNav
              sections={sections}
              activeIndex={activeIndex}
              onJump={jumpToSection}
            />
          </div>
        </aside>

        <ProjectMobileProgress
          sections={sections}
          activeIndex={activeIndex}
          onJump={jumpToSection}
          onBack={() => navigate(-1)}
        />

        <article className="project-story">
          <header className="project-hero">
            <div className="project-hero-image">
              <img src={project.hero || project.image} alt="" />
            </div>
            &nbsp; &nbsp;
            <div className="project-tags">
              {(project.tags || [project.status]).filter(Boolean).map((tag) => (
                <span key={tag}>{tag}</span>
              ))}
            </div>

            <h1>{project.title}</h1>
            <p>{project.intro || project.description}</p>

            {project.cta?.href && (
              <a className="project-cta" href={project.cta.href}>
                {project.cta.label} →
              </a>
            )}

            <div
              className="project-meta-card"
              style={{ backgroundColor: project.accent || "#121212" }}
            >
              <div>
                <span>Role</span>
                <p>{project.meta?.role || "Designer"}</p>
              </div>
              <div>
                <span>Team</span>
                <p>{project.meta?.team || "Solo"}</p>
              </div>
              <div>
                <span>Timeline</span>
                <p>{project.meta?.timeline || "2026"}</p>
              </div>
              <div>
                <span>Skills</span>
                <p>{project.meta?.skills?.join(", ") || "Design, Development"}</p>
              </div>
            </div>
          </header>

          {sections.map((section) => (
            <ProjectSectionBlock key={section.id} section={section} />
          ))}

          <RelatedProjectsSection
            currentProjectId={project.id}
            currentProjectType={project.type}
            onOpenProject={openProjectCard}
          />
        </article>
      </main>

      {gateProject && (
        <PasswordGate
          projectId={gateProject.id}
          projectTitle={gateProject.title}
          onClose={() => setGateProject(null)}
          onUnlock={() => {
            const next = gateProject.link;
            setGateProject(null);
            navigate(next);
          }}
        />
      )}
    </>
  );
}
